import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import useAuthStore from "stores/useAuthStore";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import ConfirmDialog from "components/modals/ConfirmDialog";
import { formatError } from "helpers/formatError";
import useAuthorized from "hooks/useAuthorized";
import UserService from "services/User";
import { useUsersTable, invalidateUsersQuery } from "hooks/useUsersTable";
import EditRoleModal from "routes/admin/components/EditRoleModal";
import SsoInfoModal from "routes/admin/components/SsoInfoModal";
import { USERS_PATH, USERS_TABLE_CONSTANTS } from "./UsersTableConstants";
import { UsersTableActions } from "./UsersTableActions";

const confirmDelete = async ({ userIds }) => {
  for (const userId of userIds) {
    await UserService.deleteUser(userId);
  }
  await invalidateUsersQuery();
};

const confirmResetPassword = async ({ userIds }) => {
  for (const userId of userIds) {
    await UserService.resetPassword(userId);
  }
};

const confirmDeactivate = async ({ userIds }) => {
  for (const userId of userIds) {
    await UserService.deactivateUser(userId);
  }
  await invalidateUsersQuery();
};

const confirmReactivate = async ({ userIds }) => {
  for (const userId of userIds) {
    await UserService.reactivateUser(userId);
  }
  await invalidateUsersQuery();
};

const confirmForceLogout = async ({ userIds }) => {
  for (const userId of userIds) {
    await UserService.forceLogout(userId);
  }
};

const describeConfirm = ({ actionLong, userIds, selectedUser }) =>
  userIds.length === 1 ? (
    <>
      You are about to {actionLong} user{" "}
      <strong>{selectedUser?.full_name}</strong>. Are you sure you want to
      proceed?
    </>
  ) : (
    <>
      You are about to {actionLong} <strong>{userIds.length}</strong> users. Are
      you sure you want to proceed?
    </>
  );

const UsersTableV3 = () => {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null);
  const [editRoleUserIds, setEditRoleUserIds] = useState(null);
  const [ssoMessage, setSsoMessage] = useState(null);

  const userIsAdmin = UserService.isAdmin();

  const confirmAction = useMutation({
    mutationFn: ({ onConfirm, userIds }) => onConfirm({ userIds }),
    onSuccess: () => setConfirm(null),
    onError: err => toast.error(formatError(err, "The action failed")),
  });

  // The shadcn confirm dialogs have no disabled state, so a delete that
  // includes the current user is refused before the dialog opens.
  const openConfirm = useCallback(config => {
    const currentUserId = useAuthStore.getState().userAuth?.id;
    if (config.action === "Delete" && config.userIds.includes(currentUserId)) {
      toast.error("You cannot delete yourself");
      return;
    }
    setConfirm(config);
  }, []);

  const handleEditUser = useCallback(
    user => navigate(`${USERS_PATH}/${user.id}/edit`),
    [navigate]
  );

  const handleDeleteUser = useCallback(
    user =>
      openConfirm({
        userIds: [user.id],
        title: "Delete user",
        action: "Delete",
        actionLong: "delete",
        onConfirm: confirmDelete,
        selectedUser: user,
      }),
    [openConfirm]
  );

  const handleResetPassword = useCallback(
    user => {
      if (!user.sso_bypass) {
        setSsoMessage(
          "This user authenticates via SSO (Google Sign-In). Password reset is not available for SSO-only users."
        );
        return;
      }
      openConfirm({
        userIds: [user.id],
        title: "Reset password for user",
        action: "Reset Password",
        actionLong: "reset the password for",
        onConfirm: confirmResetPassword,
        selectedUser: user,
      });
    },
    [openConfirm]
  );

  const handleDeactivateUser = useCallback(
    user =>
      openConfirm({
        userIds: [user.id],
        title: "Deactivate user",
        action: "Deactivate",
        actionLong: "deactivate",
        onConfirm: confirmDeactivate,
        selectedUser: user,
      }),
    [openConfirm]
  );

  const handleReactivateUser = useCallback(
    user =>
      openConfirm({
        userIds: [user.id],
        title: "Reactivate user",
        action: "Reactivate",
        actionLong: "reactivate",
        onConfirm: confirmReactivate,
        selectedUser: user,
      }),
    [openConfirm]
  );

  const handleForceLogout = useCallback(
    user =>
      openConfirm({
        userIds: [user.id],
        title: "Force logout user",
        action: "Force Logout",
        actionLong: "force logout",
        onConfirm: confirmForceLogout,
        selectedUser: user,
      }),
    [openConfirm]
  );

  const canResetPassword = useAuthorized([
    ACL_RELATIONSHIPS.adminUserResetPassword.create,
  ]);
  const canEditUser = useAuthorized([ACL_RELATIONSHIPS.adminUser.edit]);
  const canDeleteUser = useAuthorized([ACL_RELATIONSHIPS.adminUser.delete]);
  // Deactivation is a change of user state, so it is gated by edit.
  const canDeactivateUser = canEditUser;

  // Hook with action handlers
  const { filters, columns, customFields } = useUsersTable({
    canEditUser,
    canDeleteUser,
    canResetPassword,
    canDeactivateUser,
    onEditUser: handleEditUser,
    onDeleteUser: handleDeleteUser,
    onResetPassword: handleResetPassword,
    onDeactivateUser: handleDeactivateUser,
    onReactivateUser: handleReactivateUser,
    onForceLogout: handleForceLogout,
  });

  // Fetch users data
  const fetchUsers = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        const response = await UserService.getUsersAsAdmin(nextQueryParams);
        return response;
      } catch (error) {
        console.error("Error fetching users:", error);
        throw error;
      }
    },
    [columns]
  );

  const isDeleteConfirm = confirm?.action === "Delete";
  const runConfirm = () => {
    if (!confirmAction.isPending) confirmAction.mutate(confirm);
  };
  const closeConfirm = next => !next && setConfirm(null);

  const actionButtons = ({ selectedIds, selectedData }) => (
    <UsersTableActions
      selectedIds={selectedIds}
      selectedData={selectedData}
      canEditUser={canEditUser}
      canDeleteUser={canDeleteUser}
      canResetPassword={canResetPassword}
      userIsAdmin={userIsAdmin}
      handleEditUser={handleEditUser}
      onEditRoles={setEditRoleUserIds}
      openConfirm={openConfirm}
      confirmResetPassword={confirmResetPassword}
      confirmDelete={confirmDelete}
      confirmDeactivate={confirmDeactivate}
      confirmReactivate={confirmReactivate}
      confirmForceLogout={confirmForceLogout}
      onSsoBlock={setSsoMessage}
    />
  );

  return (
    <>
      <EditRoleModal
        open={editRoleUserIds != null}
        onOpenChange={next => !next && setEditRoleUserIds(null)}
        userIds={editRoleUserIds ?? []}
        onSuccess={() => setEditRoleUserIds(null)}
      />
      <ConfirmDeleteDialog
        open={isDeleteConfirm}
        onOpenChange={closeConfirm}
        title={confirm?.title}
        description={isDeleteConfirm ? describeConfirm(confirm) : null}
        onConfirm={runConfirm}
      />
      <ConfirmDialog
        open={confirm != null && !isDeleteConfirm}
        onOpenChange={closeConfirm}
        title={confirm?.title}
        description={
          confirm && !isDeleteConfirm ? describeConfirm(confirm) : null
        }
        confirmText={confirm?.action}
        onConfirm={runConfirm}
      />
      <SsoInfoModal message={ssoMessage} onClose={() => setSsoMessage(null)} />
      <BaseTableV3
        columns={columns}
        filters={filters}
        customFields={customFields}
        headerIcon={USERS_TABLE_CONSTANTS.headerIcon}
        header={USERS_TABLE_CONSTANTS.header}
        queryKey={USERS_TABLE_CONSTANTS.queryKey}
        className={USERS_TABLE_CONSTANTS.className}
        exportTableName={USERS_TABLE_CONSTANTS.exportTableName}
        tableName={USERS_TABLE_CONSTANTS.tableName}
        fetchData={fetchUsers}
        createButton={
          <Authorized permissions={[ACL_RELATIONSHIPS.adminUser.create]}>
            <Button size="sm" onClick={() => navigate(`${USERS_PATH}/new`)}>
              New User
            </Button>
          </Authorized>
        }
        actionButtons={actionButtons}
        invalidateData={invalidateUsersQuery}
        enableSearch
        enableSettings
      />
    </>
  );
};

export default UsersTableV3;
